import type { CurrencyCode, Money } from "./types";

/**
 * 幣別與金額
 * ═══════════════════════════════════════════════════════════════
 *
 * ── 金額存的是**該市場自己的幣別** ──────────────────────────
 *
 * 台灣的案子存台幣、日本存日圓、美國存美元。使用者選的幣別是
 * 顯示層的換算，只有在「存的幣別」和「要顯示的幣別」不同時才發生。
 *
 * 這裡原本寫的是「結算幣別固定為 USD，資料庫只存美元的分」。
 * 那個設計在一個市場隔離的平台上是多餘的一層 —— 一個台灣的
 * 接案者從頭到尾看的、談的、收的都是台幣，把它換成美元再換回來
 * 只會製造兩個問題：
 *
 *   一、每一次來回都掉精度。NT$1,500 → US$46.15 → NT$1,499.88。
 *   二、更嚴重的是**沒有人記得那條約定**。seed 寫成台幣、
 *       前端讀成美元，畫面上的每一個台灣金額都被乘了 32.5 倍，
 *       而它看起來只是「價格太高」，不像一個單位錯誤。
 *
 * ── 所以每個金額都要帶著它的幣別 ────────────────────────────
 *
 * formatMoney 的第二個參數是**來源**幣別，第三個才是要顯示成什麼。
 * 兩個都必填不是囉嗦 —— 它逼每一個呼叫點回答「這筆錢是什麼幣別」，
 * 而上面那個 32.5 倍的錯誤，正是因為沒有人被問過這個問題。
 *
 * TODO(正式上線): 匯率是寫死的靜態值。之後改成每日打一次匯率 API，
 * 結果寫進 KV，Worker 讀 KV 回給前端；簽約當下要把匯率快照寫進 contracts。
 */

export const RATES_TO_USD: Record<CurrencyCode, number> = {
  USD: 1,
  EUR: 0.92,
  GBP: 0.79,
  JPY: 157,
  TWD: 32.5,
  SGD: 1.35,
  AUD: 1.53,
};

export const CURRENCY_SYMBOLS: Record<CurrencyCode, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  TWD: "NT$",
  SGD: "S$",
  AUD: "A$",
};

/** 沒有小數位的幣別（日圓等），格式化時不顯示小數 */
/*
  沒有小數位的幣別。

  台幣在法律上有角與分，但實務上沒有人用 —— 標價、報價、
  請款單全部是整數。而換算會製造小數：US$46.15 × 32.5 =
  NT$1,499.88，那個「.88」是浮點誤差的殘渣，不是價格。
*/
const ZERO_DECIMAL: CurrencyCode[] = ["JPY", "TWD"];

export const CURRENCIES = Object.keys(RATES_TO_USD) as CurrencyCode[];

/** 建立一筆 USD 金額；輸入為「元」，內部存「分」 */
export function usd(amount: number): Money {
  return { amountMinor: Math.round(amount * 100), currency: "USD" };
}

/** 把 USD 分換算到目標幣別的「元」 */
export function convertFromUsdMinor(amountMinor: number, to: CurrencyCode): number {
  return (amountMinor / 100) * RATES_TO_USD[to];
}

/**
 * 幣別換算，以美元為樞紐。
 *
 * from === to 時直接回傳，不繞一圈 —— 繞一圈會製造浮點誤差，
 * 而絕大多數的情況是「存的和顯示的是同一個幣別」（台灣人看台幣）。
 * 那個最常見的情況必須是精確的。
 */
export function convertMinor(amountMinor: number, from: CurrencyCode, to: CurrencyCode): number {
  if (from === to) return amountMinor / 100;
  return (amountMinor / 100 / RATES_TO_USD[from]) * RATES_TO_USD[to];
}

/**
 * 格式化金額顯示。
 * @param amountMinor 最小單位，幣別是下面的 from
 * @param from 這筆錢原本的幣別（通常是市場的幣別）
 * @param to 要顯示成什麼幣別（使用者選的）
 * @param locale BCP 47，決定千分位與符號位置
 * @param compact 大數字縮寫（$1.2M），用在統計數字
 */
export function formatMoney(
  amountMinor: number,
  from: CurrencyCode,
  to: CurrencyCode,
  locale = "en",
  compact = false,
): string {
  const value = convertMinor(amountMinor, from, to);
  const zeroDecimal = ZERO_DECIMAL.includes(to);

  try {
    const parts = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: to,
      notation: compact ? "compact" : "standard",
      /*
        零小數位的幣別**永遠**沒有小數，compact 也不例外。

        compact 原本寫死 1 位。在 zh-TW 底下，一萬以下不會縮寫成
        「萬」—— 於是 NT$1,499.875 顯示成「$1499.9」，一個換算的
        殘渣就這樣留在畫面上。台灣不用角與分。
      */
      maximumFractionDigits: zeroDecimal ? 0 : compact ? 1 : value % 1 === 0 ? 0 : 2,
      minimumFractionDigits: 0,
    }).formatToParts(value);

    /*
      ── 用自己的符號蓋掉 Intl 給的 ──────────────────────────

      zh-TW 底下 Intl 給 TWD 的符號是「$」，不是「NT$」。
      在單一幣別的網站沒問題，但這個站同時有美元、台幣、新幣、
      澳幣，Intl 全部給「$」。

      台灣人看到「$50,000」不會誤會，但一個在瀏覽台灣案件的
      美國人會 —— 而那個誤會的代價是他以為這個案子值五萬美金。

      用 formatToParts 而不是自己拼字串：千分位、小數點、以及
      符號要放在數字前面還是後面，都還是交給 Intl 決定
      （法語是「50 000 €」，符號在後）。這裡只換掉符號本身。
    */
    const symbol = CURRENCY_SYMBOLS[to];
    const out = parts.map((part) => (part.type === "currency" ? symbol : part.value)).join("");

    /*
      沒有真的被縮寫的話，退回一般格式。

      縮寫的門檻跟著語系走：英文從一千開始（1.5K），中文從一萬
      開始（5萬）。所以 zh-TW 的 NT$1,500 在 compact 模式下
      不會被縮寫 —— 但它會失去千分位，變成「NT$1500」，
      而旁邊一般格式的同一個數字是「NT$1,500」。

      判斷方式是看有沒有多出「compact」這一段，而不是寫死門檻 ——
      門檻是 Intl 的實作細節，會隨語系與版本變。
    */
    if (compact && !parts.some((part) => part.type === "compact")) {
      return formatMoney(amountMinor, from, to, locale, false);
    }
    return out;
  } catch {
    // Intl 不認得該幣別時的保底顯示
    return `${CURRENCY_SYMBOLS[to]}${Math.round(value).toLocaleString()}`;
  }
}

/** 預算區間顯示：$500 – $1,200 或 $45/hr – $80/hr */
export function formatMoneyRange(
  minMinor: number,
  maxMinor: number,
  from: CurrencyCode,
  to: CurrencyCode,
  locale = "en",
  suffix = "",
): string {
  if (minMinor === maxMinor) return formatMoney(minMinor, from, to, locale) + suffix;
  return `${formatMoney(minMinor, from, to, locale)}${suffix} – ${formatMoney(maxMinor, from, to, locale)}${suffix}`;
}

/** 平台抽成（bps，1000 = 10%） */
export function platformFeeMinor(amountMinor: number, feeBps: number): number {
  return Math.round((amountMinor * feeBps) / 10_000);
}
